import {
  Component,
  OnInit,
  Input,
  ElementRef,
  Output,
  EventEmitter,
  Renderer2,
  ViewChild,
} from "@angular/core";
import { fromEvent, merge } from "rxjs";
import { filter, take, startWith, map, throttleTime } from "rxjs/operators";
export enum TypeDrag {
  Move,
  Top,
  Bottom,
  Left,
  Right,
  TopRight,
  BottomRight,
  TopLeft,
  BottomLeft,
}

@Component({
  selector: "resize-border",
  templateUrl: "./resize-border.component.html",
  styleUrls: ["./resize-border.component.scss"],
})
export class ResizeBorderComponent implements OnInit {
  rect: any;
  incr: number[] = [0, 0, 0, 0];
  nativeElement: any;
  typeDrag: TypeDrag;
  origin: any;
  onDrag: boolean = false;
  moveSubscription: any;
  modalContent: any;
  @Output() sizeStyle: any = new EventEmitter();

  classNames = [
    "cell-top",
    "cell-border-top",
    "cell-border-bottom",
    "cell-border-left",
    "cell-border-right",
    "cell-top-right",
    "cell-bottom-right",
    "cell-top-left",
    "cell-bottom-left",
  ];

  style: any = {
    width: "100%",
    height: "100%",
  };
  constructor(private elementRef: ElementRef) {}

  @Input() set dragHolder(value: {
    classList: { add: (arg0: string) => void };
  }) {
    value.classList.add("cell-top");
  }

  ngOnInit(): void {
    window.scroll({ top: 0, left: 0, behavior: "smooth" });
    this.modalContent = this.findModalContent(this.elementRef.nativeElement);
    this.modalContent.parentElement.style["max-width"] = "500px";
    this.modalContent.parentElement.style["height"] = "606px";

    // Improve performance by using passive event listeners
    merge(
      fromEvent(this.elementRef.nativeElement, "mousedown"),
      fromEvent(this.elementRef.nativeElement, "touchstart", {
        passive: false,
      }).pipe(
        map((event: TouchEvent) => ({
          target: event.target,
          screenX: event.touches[0].screenX,
          screenY: event.touches[0].screenY,
        }))
      )
    )
      .pipe(
        filter((event: MouseEvent) => {
          const classs = (event.target as any).className;
          if (classs && typeof classs === "string") {
            const className = classs.split(" ");
            return className.indexOf("cell-top") >= 0
              ? true
              : this.classNames.indexOf(classs) >= 0;
          }
          return false;
        })
      )
      .subscribe((event: MouseEvent) => {
        // Prevent default to avoid text selection and other issues
        event.preventDefault();

        this.rect = this.modalContent.getBoundingClientRect();
        this.origin = { x: event.screenX, y: event.screenY };

        this.onDrag = true;
        const className = (event.target as any).className.split(" ");
        this.typeDrag =
          className.indexOf("cell-top") >= 0
            ? TypeDrag.Move
            : (this.classNames.indexOf(className[0]) as TypeDrag);

        this.incr =
          this.typeDrag == TypeDrag.Move
            ? [1, 0, 1, 0]
            : this.typeDrag == TypeDrag.Top
            ? [1, -1, 0, 0]
            : this.typeDrag == TypeDrag.Bottom
            ? [0, 1, 0, 0]
            : this.typeDrag == TypeDrag.Right
            ? [0, 0, 0, 1]
            : this.typeDrag == TypeDrag.Left
            ? [0, 0, 1, -1]
            : this.typeDrag == TypeDrag.TopRight
            ? [1, -1, 0, 1]
            : this.typeDrag == TypeDrag.TopLeft
            ? [1, -1, 1, -1]
            : this.typeDrag == TypeDrag.BottomRight
            ? [0, 1, 0, 1]
            : [0, 1, 1, -1];

        this.onDrag = true;

        merge(fromEvent(document, "mouseup"), fromEvent(document, "touchend"))
          .pipe(take(1))
          .subscribe(() => {
            if (this.moveSubscription) {
              this.moveSubscription.unsubscribe();
              this.moveSubscription = undefined;
              this.onDrag = false;
            }
          });

        if (!this.moveSubscription) {
          this.moveSubscription = merge(
            fromEvent(document, "mousemove"),
            fromEvent(document, "touchmove").pipe(
              map((event: TouchEvent) => ({
                target: event.target,
                screenX: event.touches[0].screenX,
                screenY: event.touches[0].screenY,
              }))
            )
          )
            .pipe(
              throttleTime(16), // Throttle to ~60fps for smoother performance
              startWith({ screenY: this.origin.y, screenX: this.origin.x })
            )
            .subscribe((moveEvent: MouseEvent) => {
              // Use requestAnimationFrame to reduce lag
              requestAnimationFrame(() => {
                const incrTop = moveEvent.screenY - this.origin.y;
                const incrLeft = moveEvent.screenX - this.origin.x;

                let width = this.rect.width + this.incr[3] * incrLeft;
                let height = this.rect.height + this.incr[1] * incrTop;

                // Calculate positions
                let newTop = this.rect.top - 28 + this.incr[0] * incrTop;
                let newLeft = this.rect.left + this.incr[2] * incrLeft;

                // Get viewport dimensions
                const viewportWidth = window.innerWidth;
                const viewportHeight = window.innerHeight;

                // Minimum sizes
                const minWidth = 300;
                const minHeight = 200;

                // Apply minimum constraints
                width = Math.max(minWidth, width);
                height = Math.max(minHeight, height);

                // Prevent moving out of screen bounds
                if (this.typeDrag === TypeDrag.Move) {
                  // For move operation, constrain position
                  newTop = Math.max(
                    0,
                    Math.min(newTop, viewportHeight - height - 47)
                  );
                  newLeft = Math.max(
                    0,
                    Math.min(newLeft, viewportWidth - width)
                  );
                } else {
                  // For resize operations, constrain size to fit within viewport
                  if (newLeft < 0) {
                    width += newLeft;
                    newLeft = 0;
                  }
                  if (newTop < 0) {
                    height += newTop;
                    newTop = 0;
                  }

                  // Ensure element doesn't exceed viewport
                  width = Math.min(width, viewportWidth - newLeft);
                  height = Math.min(height, viewportHeight - newTop - 47);

                  // Re-apply minimum constraints
                  width = Math.max(minWidth, width);
                  height = Math.max(minHeight, height);
                }

                // Apply styles using transform for better performance
                const parentElement = this.modalContent.parentElement;
                parentElement.style.maxWidth = width + "px";
                parentElement.style.height = height + 47 + "px";
                parentElement.style.marginTop = newTop + "px";
                parentElement.style.marginLeft = newLeft + "px";

                this.modalContent.style.maxWidth = width + "px";
                this.modalContent.style.height = height + "px";

                // Emit size change
                this.sizeStyle.emit({
                  width: `calc(${width}px)`,
                  height: `calc(${height}px - 57px)`,
                });
              });
            });
        }
      });
  }

  findModalContent(element: HTMLElement) {
    return element.getAttribute("role") == "document" ||
      element.className == "modal-dialog"
      ? element
      : element.parentElement
      ? this.findModalContent(element.parentElement)
      : null;
  }
}
